import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { BarChart3, Handshake, Target, TrendingUp } from 'lucide-react'

import type { LeadSource } from '@/types/marketing'
import { LEAD_SOURCE_LABELS } from '@/types/marketing'
import { useAttribution } from '@/features/marketing/hooks'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const sourceLabel = (s: string) =>
  s === '' ? 'Unknown' : (LEAD_SOURCE_LABELS[s as LeadSource] ?? s)

const dollars = (cents: number) => `$${Math.round(cents / 100).toLocaleString()}`

function monthLabel(month: string) {
  const [y, m] = month.split('-')
  const date = new Date(Number(y), Number(m) - 1, 1)
  return date.toLocaleDateString(undefined, { month: 'short' })
}

export function SourcesTab() {
  const { data, isLoading } = useAttribution(12)

  const byMonth = useMemo(() => {
    if (!data) return []
    // Reshape the (month, source, leads) points into one row per month with
    // a column per source — the shape Recharts stacks.
    const months = new Map<string, Record<string, string | number>>()
    for (const point of data.months) {
      let row = months.get(point.month)
      if (!row) {
        row = { month: monthLabel(point.month) }
        months.set(point.month, row)
      }
      row[sourceLabel(point.source)] = point.leads
    }
    return [...months.values()]
  }, [data])

  const stackedSources = useMemo(() => {
    if (!data) return []
    return [...new Set(data.months.map((p) => sourceLabel(p.source)))]
  }, [data])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const totals = data?.sources.reduce(
    (acc, s) => ({
      leads: acc.leads + s.leads,
      converted: acc.converted + s.leads_converted,
      customers: acc.customers + s.customers,
      wonValue: acc.wonValue + s.won_value_cents,
    }),
    { leads: 0, converted: 0, customers: 0, wonValue: 0 },
  ) ?? { leads: 0, converted: 0, customers: 0, wonValue: 0 }

  const rows = [...(data?.sources ?? [])].sort((a, b) => b.leads + b.customers - (a.leads + a.customers))
  const topSource = rows.find((r) => sourceLabel(r.source) !== 'Unknown' && r.leads + r.customers > 0)
  const referralShare =
    totals.customers > 0
      ? Math.round(
          ((rows.find((r) => r.source === 'referral')?.customers ?? 0) / totals.customers) * 100,
        )
      : 0

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard
          icon={<Target className="h-4 w-4" />}
          label="Leads captured"
          value={String(totals.leads)}
          sub={`${totals.converted} converted to customers`}
        />
        <SummaryCard
          icon={<BarChart3 className="h-4 w-4" />}
          label="Top source"
          value={topSource ? sourceLabel(topSource.source) : '—'}
          sub={topSource ? `${topSource.leads} leads · ${topSource.customers} customers` : 'No data yet'}
        />
        <SummaryCard
          icon={<Handshake className="h-4 w-4" />}
          label="Referral customers"
          value={`${referralShare}%`}
          sub={`${data?.referrals.total ?? 0} referrals tracked`}
        />
        <SummaryCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Won revenue by source"
          value={dollars(totals.wonValue)}
          sub="Deals won by attributed customers"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" /> Leads captured by source
          </CardTitle>
          <CardDescription>
            Monthly intake volume per acquisition source — the stacked shape shows which channels
            actually deliver prospects.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {byMonth.length === 0 ? (
            <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
              No leads captured in this window.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byMonth}>
                <CartesianGrid {...CHART_GRID_PROPS} />
                <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip {...CHART_TOOLTIP_PROPS} />
                {stackedSources.map((source, i) => (
                  <Bar
                    key={source}
                    dataKey={source}
                    stackId="leads"
                    fill={`hsl(var(--chart-${(i % 5) + 1}))`}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead className="text-right">Lead → customer</TableHead>
              <TableHead className="text-right">Customers</TableHead>
              <TableHead className="text-right">Deals</TableHead>
              <TableHead className="text-right">Won</TableHead>
              <TableHead className="text-right">Won value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-24 text-center text-sm text-muted-foreground"
                >
                  No acquisition data yet — sources appear as leads come in and customers are
                  attributed.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.source || 'unknown'}>
                  <TableCell className="font-medium">{sourceLabel(row.source)}</TableCell>
                  <TableCell className="text-right">{row.leads}</TableCell>
                  <TableCell className="text-right">
                    {row.leads > 0 ? `${Math.round(row.conversion_rate * 100)}%` : '—'}
                  </TableCell>
                  <TableCell className="text-right">{row.customers}</TableCell>
                  <TableCell className="text-right">{row.deals}</TableCell>
                  <TableCell className="text-right">{row.won}</TableCell>
                  <TableCell className="text-right">{dollars(row.won_value_cents)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function SummaryCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub: string
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="text-xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  )
}
